import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Users,
  Package,
  TrendingUp,
  Activity,
  AlertTriangle,
  ChevronRight,
  RefreshCw,
  Clock,
  Database,
  Zap,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { MetricCard, StatusCard } from '../../components/admin/cards';
import { LineChart, PieChart } from '../../components/admin/charts';
import { api } from '../../services/api';
import { HealthGauge, LiveIndicator } from '../../components/admin/shared';

type TimeRange = '7d' | '30d' | '90d';

export default function AdminDashboard() {
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');

  const {
    data: metrics,
    isLoading,
  } = useQuery({
    queryKey: ['admin-overview-metrics'],
    queryFn: () => api.getAdminOverview(),
    refetchInterval: 60000,
  });

  // Live Operations Data
  const { data: liveStatus } = useQuery({
    queryKey: ['ops-live-status'],
    queryFn: () => api.getOpsLiveStatus(),
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  const { data: alerts } = useQuery({
    queryKey: ['ops-alerts'],
    queryFn: () => api.getOpsAlerts(),
    refetchInterval: 30000,
  });

  const { data: growthData } = useQuery({
    queryKey: ['admin-user-growth', timeRange],
    queryFn: () => api.getAdminUserGrowth(timeRange),
  });

  const { data: topWatched } = useQuery({
    queryKey: ['admin-top-watched'],
    queryFn: () => api.getAdminTopWatched(5),
  });

  const tierData = metrics
    ? [
        { name: 'Free', value: metrics.tiers.free, color: '#9ca3af' },
        { name: 'Hunter', value: metrics.tiers.hunter, color: '#0077B6' },
        { name: 'Pro', value: metrics.tiers.pro, color: '#10b981' },
        { name: 'Dealer', value: metrics.tiers.dealer, color: '#F4A261' },
      ].filter((t) => t.value > 0)
    : [];

  const formatChartDate = (date: string) => {
    const d = new Date(date);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  };

  const chartData =
    growthData?.daily.map((d) => ({
      ...d,
      date: formatChartDate(d.date),
    })) || [];

  // Count critical and warning alerts
  const alertsList = alerts?.alerts || [];
  const criticalAlerts = alertsList.filter(a => a.severity === 'critical').length;
  const warningAlerts = alertsList.filter(a => a.severity === 'warning').length;

  // Calculate health scores from live status
  const runningSeeds = liveStatus?.seeds?.running || 0;
  const queuedSeeds = liveStatus?.seeds?.queued || 0;
  const staleProducts = liveStatus?.data?.staleProducts || 0;
  const totalProducts = liveStatus?.data?.productsUpdated24h || 0;
  const totalStores = liveStatus?.data?.totalStores || 1;
  const storesCovered = liveStatus?.data?.storesCovered || 0;
  // Calculate percentages
  const dataFreshness = totalProducts > 0 ? Math.round((totalProducts / (totalProducts + staleProducts)) * 100) : 0;
  const storeCoverage = totalStores > 0 ? Math.round((storesCovered / totalStores) * 100) : 0;
  const errorCount24h = liveStatus?.seeds?.errorsLast24h || 0;

  return (
    <div className="space-y-6">
      {/* Quick Status Bar - Link to Operations Center */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <LiveIndicator />
              <span className="text-sm font-medium text-gray-700">Live Status</span>
            </div>

            <div className="flex items-center gap-4 divide-x divide-gray-200">
              {/* Running Seeds */}
              <div className="flex items-center gap-2 pl-4">
                <div className={`w-2 h-2 rounded-full ${runningSeeds > 0 ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
                <span className="text-sm text-gray-600">
                  <span className="font-semibold text-gray-900">{runningSeeds}</span> running
                </span>
              </div>

              {/* Queued Seeds */}
              <div className="flex items-center gap-2 pl-4">
                <Clock size={14} className="text-gray-400" />
                <span className="text-sm text-gray-600">
                  <span className="font-semibold text-gray-900">{queuedSeeds}</span> queued
                </span>
              </div>

              {/* Alerts */}
              <div className="flex items-center gap-2 pl-4">
                {criticalAlerts > 0 ? (
                  <div className="flex items-center gap-1 text-red-600">
                    <AlertTriangle size={14} />
                    <span className="text-sm font-semibold">{criticalAlerts} critical</span>
                  </div>
                ) : warningAlerts > 0 ? (
                  <div className="flex items-center gap-1 text-amber-600">
                    <AlertTriangle size={14} />
                    <span className="text-sm font-semibold">{warningAlerts} warnings</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-green-600">
                    <CheckCircle2 size={14} />
                    <span className="text-sm font-medium">All clear</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <Link
            to="/admin/ops"
            className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            Operations Center
            <ChevronRight size={16} />
          </Link>
        </div>
      </div>

      {/* System Health KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Data Freshness</span>
            <Database size={16} className="text-gray-400" />
          </div>
          <div className="flex items-center gap-3">
            <HealthGauge value={dataFreshness} size="sm" />
            <div>
              <p className="text-lg font-bold text-gray-900">{dataFreshness}%</p>
              <p className="text-xs text-gray-500">updated in 24h</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Stale Products</span>
            <RefreshCw size={16} className="text-gray-400" />
          </div>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              staleProducts === 0 ? 'bg-green-100' : staleProducts < 100 ? 'bg-amber-100' : 'bg-red-100'
            }`}>
              {staleProducts === 0 ? (
                <CheckCircle2 size={20} className="text-green-600" />
              ) : (
                <XCircle size={20} className={staleProducts < 100 ? 'text-amber-600' : 'text-red-600'} />
              )}
            </div>
            <div>
              <p className="text-lg font-bold text-gray-900">{staleProducts.toLocaleString()}</p>
              <p className="text-xs text-gray-500">need refresh</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Store Coverage</span>
            <Zap size={16} className="text-gray-400" />
          </div>
          <div className="flex items-center gap-3">
            <HealthGauge value={storeCoverage} size="sm" />
            <div>
              <p className="text-lg font-bold text-gray-900">{storeCoverage}%</p>
              <p className="text-xs text-gray-500">stores active</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Errors (24h)</span>
            <AlertTriangle size={16} className="text-gray-400" />
          </div>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              errorCount24h === 0 ? 'bg-green-100' :
              errorCount24h < 10 ? 'bg-amber-100' : 'bg-red-100'
            }`}>
              {errorCount24h === 0 ? (
                <CheckCircle2 size={20} className="text-green-600" />
              ) : (
                <span className={`text-sm font-bold ${
                  errorCount24h < 10 ? 'text-amber-600' : 'text-red-600'
                }`}>
                  {errorCount24h}
                </span>
              )}
            </div>
            <div>
              <p className="text-lg font-bold text-gray-900">{errorCount24h}</p>
              <p className="text-xs text-gray-500">
                <Link to="/admin/errors" className="text-primary-600 hover:underline">
                  View errors →
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Users"
          value={isLoading ? '...' : metrics?.users.total || 0}
          subtitle={`+${metrics?.users.newThisWeek || 0} this week`}
          icon={<Users size={24} />}
          status="success"
        />
        <MetricCard
          title="Products Tracked"
          value={isLoading ? '...' : metrics?.products.total || 0}
          subtitle={`${metrics?.products.asIs || 0} As-Is, ${metrics?.products.clearance || 0} Clearance`}
          icon={<Package size={24} />}
          status="neutral"
        />
        <MetricCard
          title="Contributions Today"
          value={isLoading ? '...' : metrics?.contributions.today || 0}
          subtitle={`${metrics?.contributions.thisWeek || 0} this week`}
          icon={<TrendingUp size={24} />}
          status="success"
        />
        <MetricCard
          title="Seed Success Rate"
          value={isLoading ? '...' : `${metrics?.system.seedSuccessRate || 100}%`}
          subtitle={
            metrics?.system.lastSeedRun
              ? `Last run: ${new Date(metrics.system.lastSeedRun).toLocaleString()}`
              : 'No recent runs'
          }
          icon={<Activity size={24} />}
          status={
            (metrics?.system.seedSuccessRate || 100) >= 90
              ? 'success'
              : (metrics?.system.seedSuccessRate || 100) >= 70
              ? 'warning'
              : 'error'
          }
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">User Growth</h3>
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                {(['7d', '30d', '90d'] as TimeRange[]).map((range) => (
                  <button
                    key={range}
                    onClick={() => setTimeRange(range)}
                    className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                      timeRange === range
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {range}
                  </button>
                ))}
              </div>
            </div>
            <LineChart
              data={chartData}
              xKey="date"
              yKey="cumulative"
              height={250}
            />
          </div>
        </div>
        <PieChart data={tierData} title="Tier Breakdown" height={280} />
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Watched Products */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Top Watched Products
          </h3>
          <div className="space-y-4">
            {topWatched?.slice(0, 5).map((product, index) => (
              <div
                key={product.productCode}
                className="flex items-center gap-4"
              >
                <span className="text-sm font-medium text-gray-500 w-6">
                  {index + 1}.
                </span>
                {product.imageUrl && (
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="w-10 h-10 rounded object-cover"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {product.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {product.productCode}
                  </p>
                </div>
                <span className="text-sm font-semibold text-primary-600">
                  {product.watcherCount} watchers
                </span>
              </div>
            )) || (
              <p className="text-gray-500 text-sm">No watched products yet</p>
            )}
          </div>
        </div>

        {/* System Status */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">
            System Status
          </h3>
          <div className="grid gap-4">
            <StatusCard
              title="API"
              status={metrics?.system.apiStatus === 'healthy' ? 'healthy' : 'error'}
              details={[
                { label: 'Status', value: metrics?.system.apiStatus || 'Unknown' },
              ]}
            />
            <StatusCard
              title="Seed Browsers"
              status={
                (metrics?.system.seedSuccessRate || 100) >= 90
                  ? 'healthy'
                  : (metrics?.system.seedSuccessRate || 100) >= 70
                  ? 'warning'
                  : 'error'
              }
              details={[
                {
                  label: 'Success Rate',
                  value: `${metrics?.system.seedSuccessRate || 100}%`,
                },
                {
                  label: 'Last Run',
                  value: metrics?.system.lastSeedRun
                    ? new Date(metrics.system.lastSeedRun).toLocaleTimeString()
                    : 'N/A',
                },
              ]}
            />
            <StatusCard
              title="Database"
              status={liveStatus?.system?.apiStatus === 'healthy' ? 'healthy' : 'warning'}
              details={[
                { label: 'Status', value: liveStatus?.system?.apiStatus || 'Unknown' },
                { label: 'Freshness', value: `${dataFreshness}% updated` },
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
